import { effect, Injectable, signal } from '@angular/core';
import { STORAGE_KEYS, StorageKey } from '@core/constants/storage-keys.constants';
import { FeatureFlag } from '../models/authentication.model';
import { EncryptionService } from './encryption.service';
import { NewRelicService } from '@app/shared/services/newrelic.service';

interface SessionData {
  timestamp: number;
  lastActivity: number;
}

@Injectable({
  providedIn: 'root'
})
export class SessionStorageService {

  constructor(private encryptionService: EncryptionService, private newrelicService: NewRelicService) { }


  createSignal<T>(key: StorageKey, defaultValue: T) {
    // Initialize from storage
    const stored = this.getItem<T>(key);
    const state = signal<T>(stored ?? defaultValue);

    // Auto-save on change
    effect(() => {
      const value = state();
      this.setItem(key, value);
    });

    return state;
  }

  getItem<T>(key: StorageKey): T | null {
    try {
      const encrypted = sessionStorage.getItem(key);

      if (!encrypted) {
        return null;
      }

      const decrypted = this.encryptionService.decrypt<T>(encrypted);
      return decrypted;
    } catch (error) {
      return null;
    }
  }

  setItem<T>(key: StorageKey, value: T): void {
    try {
      const encrypted = this.encryptionService.encrypt(value);
      sessionStorage.setItem(key, encrypted);
    } catch (error) {
    }
  }

  removeItem(key: StorageKey): void {
    sessionStorage.removeItem(key);
  }

  clear(): void {
    Object.values(STORAGE_KEYS).forEach(key => {
      sessionStorage.removeItem(key);
    });
  }

  clearAppData(): void {
    Object.values(STORAGE_KEYS).forEach(key => {
      this.removeItem(key as StorageKey);
    });
  }

  hasItem(key: StorageKey): boolean {
    return sessionStorage.getItem(key) !== null;
  }

  // ===== Convenience Methods =====

  getBenifitType(): string | null {
    return this.getItem<string>(STORAGE_KEYS.BENIFIT_TYPE);
  }

  getPhoneNumber(): string | null {
    return this.getItem<string>(STORAGE_KEYS.PHONE_DETAIL);
  }

  getSessionId(): string | null {
    return this.getItem<string>(STORAGE_KEYS.SESSION_ID);
  }

  getSessionData(): SessionData | null {
    return this.getItem<SessionData>(STORAGE_KEYS.SESSION_DATA);
  }

  getFeatureFlag(): FeatureFlag | null {
    return this.getItem<FeatureFlag>(STORAGE_KEYS.FEATURE_FLAG);
  }

  setSessionId(sessionId: string): void {
    const sessionData: SessionData = {
      timestamp: Date.now(),
      lastActivity: Date.now()
    };
    //adding session id to newrelic for logging purpose
    this.newrelicService.setGlobalAttributes({ claimSessionId: sessionId })
    this.setItem(STORAGE_KEYS.SESSION_ID, sessionId);
    this.setItem(STORAGE_KEYS.SESSION_DATA, sessionData);
  }

  updateLastActivity(): void {
    const sessionData = this.getSessionData();
    if (sessionData) {
      sessionData.lastActivity = Date.now();
      this.setItem(STORAGE_KEYS.SESSION_DATA, sessionData);
    }
  }

  /**
   * ✅ Get all encrypted keys (for debugging)
   */
  getEncryptedKeys(): string[] {
    const keys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key) {
        keys.push(key);
      }
    }
    return keys;
  }

  /**
   * ✅ Verify encryption (for debugging)
   */
  verifyEncryption(key: StorageKey): boolean {
    try {
      const encrypted = sessionStorage.getItem(key);
      if (!encrypted) {
        return false;
      }

      const isEncrypted = this.encryptionService.isEncrypted(encrypted);
      return isEncrypted;
    } catch (error) {
      console.error(`❌ Encryption verification failed for ${key}:`, error);
      return false;
    }
  }
}